import { Node, Edge } from 'reactflow';
import type { CardSurvey, CardQuestion, SurveyPath } from '../types';
import { normalizeTriggerValues } from './pathTriggerValues';

export interface QuestionSummary {
  id: string;
  number: number | string;
  text: string;
  type: string;
  options?: string[];
  format?: string;
  rateAmount?: number;
}

export interface PathSummary {
  triggerQuestionId: string;
  option: string;
  questionIds: string[];
}

export interface SurveySummary {
  questionIds: string[];
  questions: Record<string, QuestionSummary>;
  paths: PathSummary[];
}

export interface MigrationComparisonResult {
  match: boolean;
  oldSummary: SurveySummary;
  newSummary: SurveySummary;
  missingInNew: string[];
  addedInNew: string[];
  missingPathsInNew: PathSummary[];
  addedPathsInNew: PathSummary[];
  differences: Array<{
    questionId: string;
    field: string;
    oldValue: string | undefined;
    newValue: string | undefined;
  }>;
}

function isConditionalEdge(edge: Edge): boolean {
  return (
    edge.data?.isConditional === true ||
    (edge.data?.conditionalOption != null && edge.data?.conditionalOption !== 'else')
  );
}

function parseQuestionTextFromLabel(label: string): string {
  const m = label.match(/^Q[\d.]+:\s*(.+)$/s);
  return m ? normalizeDisplayText(m[1].trim()) : normalizeDisplayText(label);
}

/** Strip "Qx.x:" prefix and "Type: ..." suffix so summary text is comparable and not duplicated in display */
function normalizeDisplayText(text: string): string {
  let out = (text || '').trim();
  out = out.replace(/^Q\d+(?:\.\d+)?:\s*/i, '').trim();
  out = out.replace(/\s*\n?\s*Type:\s*.+$/i, '').trim();
  return out || (text || '').trim();
}

function extractNumericId(id: string): number {
  const n = parseFloat(id);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Build a comparable summary from old flow payload (nodes + edges)
 */
export function buildOldSummaryFromFlow(nodes: Node[], edges: Edge[]): SurveySummary {
  const questionNodes = nodes.filter(
    (n) => n.id !== 'start' && n.id !== 'end' && (n.type === 'question' || (!n.type && n.id !== 'start' && n.id !== 'end'))
  );
  const questionIds: string[] = [];
  const questions: Record<string, QuestionSummary> = {};

  questionNodes.forEach((node) => {
    const meta = node.data?.meta as Record<string, unknown> | undefined;
    const id = node.id;
    const isConditionalNode = id.includes('.');
    questionIds.push(id);
    const num = meta?.number != null ? (meta.number as number) : extractNumericId(id);
    const rawText =
      (isConditionalNode && (meta?.response_question as string)) ||
      (meta?.question as string) ||
      (node.data?.question as string) ||
      parseQuestionTextFromLabel((node.data?.label as string) || '');
    const text = normalizeDisplayText(rawText);
    const type = (meta?.type as string) || 'textfield';
    const typeConfig = (meta?.type_config as Record<string, unknown>) || {};
    const options =
      (typeConfig.radio_options as string[]) || (typeConfig.checkbox_options as string[]) || undefined;
    const format = typeConfig.format as string | undefined;
    const rateAmount = typeConfig.rate_amount as number | undefined;

    questions[id] = {
      id,
      number: num,
      text: (text || '').trim(),
      type,
      options,
      format,
      rateAmount,
    };
  });

  // Paths: edges that target a sub-question (id contains .) and have conditional option
  const pathMap = new Map<string, PathSummary>();
  edges.forEach((edge) => {
    if (!isConditionalEdge(edge) || !edge.target) return;
    const targetId = edge.target as string;
    if (!targetId.includes('.')) return;
    const sourceId = edge.source as string;
    const option =
      (typeof edge.label === 'string' ? edge.label : '') ||
      (edge.data?.conditionalOption != null ? String(edge.data.conditionalOption) : '') ||
      '';
    const key = `${sourceId}::${option}`;
    if (!pathMap.has(key)) {
      pathMap.set(key, { triggerQuestionId: sourceId, option, questionIds: [] });
    }
    const path = pathMap.get(key)!;
    if (!path.questionIds.includes(targetId)) {
      path.questionIds.push(targetId);
    }
  });

  // Sort path question ids by numeric order (2.1 before 2.2)
  pathMap.forEach((path) => {
    path.questionIds.sort((a, b) => {
      const na = parseFloat(a);
      const nb = parseFloat(b);
      return Number.isFinite(na) && Number.isFinite(nb) ? na - nb : a.localeCompare(b);
    });
  });

  const paths = Array.from(pathMap.values());
  return { questionIds, questions, paths };
}

/**
 * Build a comparable summary from CardSurvey (new format)
 */
export function buildNewSummaryFromCard(card: CardSurvey): SurveySummary {
  const questionIds = card.questions.map((q) => q.id);
  const questions: Record<string, QuestionSummary> = {};
  card.questions.forEach((q: CardQuestion) => {
    questions[q.id] = {
      id: q.id,
      number: q.number,
      text: (q.text || '').trim(),
      type: q.type,
      options: q.config?.options,
      format: q.config?.format,
      rateAmount: q.config?.rateAmount,
    };
  });

  const paths: PathSummary[] = (card.paths || []).map((p: SurveyPath) => ({
    triggerQuestionId: p.trigger.questionId,
    option: normalizeTriggerValues(p.trigger.values)[0] ?? '',
    questionIds: [...(p.questionIds || [])],
  }));

  return { questionIds, questions, paths };
}

function pathKey(p: PathSummary): string {
  return `${p.triggerQuestionId}::${p.option}::${p.questionIds.join(',')}`;
}

function pathsEqual(a: PathSummary, b: PathSummary): boolean {
  if (a.triggerQuestionId !== b.triggerQuestionId || a.option !== b.option) return false;
  if (a.questionIds.length !== b.questionIds.length) return false;
  return a.questionIds.every((id, i) => id === b.questionIds[i]);
}

/**
 * Compare old summary vs new summary and return missing/added/differences
 */
export function compareOldNewSummaries(
  oldSummary: SurveySummary,
  newSummary: SurveySummary
): MigrationComparisonResult {
  const missingInNew: string[] = [];
  const addedInNew: string[] = [];
  const differences: MigrationComparisonResult['differences'] = [];

  const oldIds = new Set(oldSummary.questionIds);
  const newIds = new Set(newSummary.questionIds);

  oldIds.forEach((id) => {
    if (!newIds.has(id)) missingInNew.push(id);
  });
  newIds.forEach((id) => {
    if (!oldIds.has(id)) addedInNew.push(id);
  });

  oldSummary.questionIds.forEach((id) => {
    const o = oldSummary.questions[id];
    const n = newSummary.questions[id];
    if (!o || !n) return;
    if ((o.text || '').trim() !== (n.text || '').trim()) {
      differences.push({ questionId: id, field: 'text', oldValue: o.text, newValue: n.text });
    }
    if (o.type !== n.type) {
      differences.push({ questionId: id, field: 'type', oldValue: o.type, newValue: n.type });
    }
    const oOpts = (o.options || []).join('|');
    const nOpts = (n.options || []).join('|');
    if (oOpts !== nOpts) {
      differences.push({
        questionId: id,
        field: 'options',
        oldValue: oOpts || undefined,
        newValue: nOpts || undefined,
      });
    }
    if (o.format !== n.format) {
      differences.push({
        questionId: id,
        field: 'format',
        oldValue: o.format,
        newValue: n.format,
      });
    }
    if (o.rateAmount !== n.rateAmount) {
      differences.push({
        questionId: id,
        field: 'rateAmount',
        oldValue: o.rateAmount != null ? String(o.rateAmount) : undefined,
        newValue: n.rateAmount != null ? String(n.rateAmount) : undefined,
      });
    }
  });

  const oldPathKeys = new Set(oldSummary.paths.map(pathKey));
  const newPathKeys = new Set(newSummary.paths.map(pathKey));
  const missingPathsInNew = oldSummary.paths.filter((p) => {
    const key = pathKey(p);
    if (!newPathKeys.has(key)) return true;
    const newPath = newSummary.paths.find((np) => pathKey(np) === key);
    return newPath ? !pathsEqual(p, newPath) : true;
  });
  const addedPathsInNew = newSummary.paths.filter((p) => {
    const key = pathKey(p);
    if (!oldPathKeys.has(key)) return true;
    const oldPath = oldSummary.paths.find((op) => pathKey(op) === key);
    return oldPath ? !pathsEqual(oldPath, p) : true;
  });

  const match =
    missingInNew.length === 0 &&
    missingPathsInNew.length === 0 &&
    differences.length === 0;

  return {
    match,
    oldSummary,
    newSummary,
    missingInNew,
    addedInNew,
    missingPathsInNew,
    addedPathsInNew,
    differences,
  };
}
